import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl } from '@angular/forms';

export interface SelectOption {
  value: string | number | null;
  label: string;
  disabled?: boolean;
}

export type SelectSize = 'sm' | 'md' | 'lg';

/**
 * Select Component
 *
 * A reusable select dropdown component with consistent styling and validation.
 *
 * Usage:
 * ```html
 * <!-- Basic select -->
 * <app-select
 *   label="Country"
 *   [options]="countryOptions"
 *   placeholder="Select a country"
 *   formControlName="country">
 * </app-select>
 *
 * <!-- With validation -->
 * <app-select
 *   label="Category"
 *   [options]="categoryOptions"
 *   [control]="form.controls.category"
 *   [required]="true"
 *   formControlName="category">
 * </app-select>
 * ```
 *
 * In component:
 * ```typescript
 * countryOptions: SelectOption[] = [
 *   { value: 'ch', label: 'Switzerland' },
 *   { value: 'de', label: 'Germany' },
 *   { value: 'at', label: 'Austria' }
 * ];
 * ```
 */
@Component({
  selector: 'app-select',
  standalone: false,
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ]
})
export class SelectComponent implements ControlValueAccessor, OnInit {
  /** Select label */
  @Input() label = '';

  /** Select options */
  @Input() options: SelectOption[] = [];

  /** Placeholder text for empty selection */
  @Input() placeholder = '';

  /** Select size */
  @Input() size: SelectSize = 'md';

  /** Whether the field is required */
  @Input() required = false;

  /** Whether the select is disabled */
  @Input() disabled = false;

  /** Helper hint text */
  @Input() hint = '';

  /** Form control for validation */
  @Input() control: AbstractControl | null = null;

  /** When to show validation errors */
  @Input() showErrorWhen: 'touched' | 'dirty' | 'always' = 'touched';

  /** Custom error message */
  @Input() customError = '';

  /** Unique ID for the select */
  @Input() inputId = '';

  /** Internal selected value */
  selectedValue: string | number | null = null;

  /** Generated ID */
  generatedId = '';

  // ControlValueAccessor callbacks - initialized as noop, overwritten by registerOnChange/registerOnTouched
  private onChange: (value: string | number | null) => void = () => { /* noop */ };
  private onTouched: () => void = () => { /* noop */ };

  ngOnInit(): void {
    this.generatedId = this.inputId || `select-${Math.random().toString(36).substr(2, 9)}`;
  }

  /** Get size class */
  get sizeClass(): string {
    if (this.size === 'sm') return 'form-select-sm';
    if (this.size === 'lg') return 'form-select-lg';
    return '';
  }

  /** Check if select has error state */
  get hasError(): boolean {
    if (!this.control) return false;

    switch (this.showErrorWhen) {
      case 'touched':
        return this.control.invalid && this.control.touched;
      case 'dirty':
        return this.control.invalid && this.control.dirty;
      case 'always':
        return this.control.invalid;
      default:
        return false;
    }
  }

  /** Get error message */
  get errorMessage(): string {
    if (this.customError) return this.customError;
    if (!this.control?.errors) return '';

    const errors = this.control.errors;

    if (errors['required']) return `${this.label || 'This field'} is required.`;

    return 'Invalid selection.';
  }

  /** Handle select change */
  onSelectChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const value = target.value === '' ? null : target.value;
    this.selectedValue = value;
    this.onChange(value);
  }

  /** Handle blur */
  onBlur(): void {
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: string | number | null): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
